'use client'

// Who is this? — on hover, anywhere a Twetch name or face appears in /alkanes.
//
// The card answers with the person (name, bio, reach) and, when their picture
// is a Bitcoin inscription, with the artifact itself: its name, collection and
// inscription number. Pointer-hover only by design — on touch, the same
// trigger's tap already opens the full profile, which carries the same facts.

import React from 'react'
import Link from 'next/link'
import useSwr from 'swr'
import * as HoverCard from '@radix-ui/react-hover-card'
import { BadgeCheck } from 'lucide-react'
import { twetchJson, type TwetchUser } from '../lib/twetch-api'
import { resolveTwetchImage } from '../lib/twetch-media'
import { openTwetchUserProfile } from '../lib/twetch-profile'
import { numCompact } from '../lib/format'
import useTwetchPfp from '../lib/use-twetch-pfp'
import FollowButton from './follow-button'
import featureFlags from '@/utils/feature-flags'

/** Profiles and follows are parked: hover cards, follow buttons and follower
 * counts stay hidden unless NEXT_PUBLIC_FEATURES lists this. */
export const SOCIAL_PROFILES_FLAG = 'social::profiles'

interface TwetchUserDetail extends TwetchUser {
	description?: string | null
	numFollowers?: number
	numFollowing?: number
}

function CardBody({ userId, seed }: { userId: number; seed?: TwetchUser }) {
	const { data } = useSwr<TwetchUserDetail>(
		`alkanes-twetch-profile-${userId}`,
		() => twetchJson<TwetchUserDetail>(`/v1/users/${userId}`),
		{ revalidateOnFocus: false }
	)
	const user: TwetchUserDetail = { ...seed, ...data, id: userId }
	const pfp = useTwetchPfp(user.icon)
	const face = resolveTwetchImage(user.icon)
	const [faceBroken, setFaceBroken] = React.useState(false)
	const name = user.name ?? `#${userId}`

	return (
		<div data-testid="alkanes-user-hover-card" className="w-[288px] p-4">
			<div className="flex items-center gap-3">
				<span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-full bg-secondary text-xs font-bold text-muted-foreground">
					{face && !faceBroken ? (
						<img
							src={face}
							alt=""
							onError={() => setFaceBroken(true)}
							className="size-full object-cover [image-rendering:pixelated]"
						/>
					) : (
						name.slice(0, 2).toUpperCase()
					)}
				</span>
				{/* `block`, explicitly: the site's stylesheet makes every <p> an
				    inline-block, which ran the name and the counts together as
				    "cunt4 followers". */}
				<div className="min-w-0">
					<p className="block truncate text-sm font-bold leading-tight">{name}</p>
					<p className="mt-0.5 block text-xs tabular-nums text-muted-foreground">
						{data ? (
							<>
								<span className="font-semibold text-foreground">{numCompact(user.numFollowers ?? 0)}</span>{' '}
								followers · <span className="font-semibold text-foreground">{numCompact(user.numFollowing ?? 0)}</span>{' '}
								following
							</>
						) : (
							`Twetch #${userId}`
						)}
					</p>
				</div>
			</div>

			{user.description && (
				<p className="mt-3 block line-clamp-3 whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">
					{user.description}
				</p>
			)}

			{/* What the picture IS, in one line: the card already shows it, so
			    showing it again only said the same thing twice. This says the
			    thing the image cannot — that it is an inscription this wallet
			    holds, which one, and it opens it. */}
			{pfp && (
				<Link
					href={`/inscription/${pfp.id}`}
					data-testid="alkanes-user-hover-pfp"
					title={pfp.collection?.name ? `${pfp.collection.name} · ${pfp.id}` : pfp.id}
					className="mt-3 flex items-center gap-1.5 text-xxs text-muted-foreground transition-colors hover:text-foreground"
				>
					<BadgeCheck className="size-3.5 shrink-0 text-primary" aria-hidden="true" />
					<span className="truncate">
						<span className="font-semibold text-foreground">
							{pfp.collection?.name ?? pfp.name ?? 'Inscription'}
						</span>
						{pfp.num !== null ? ` · #${pfp.num.toLocaleString()}` : ` · ${pfp.id.slice(0, 6)}…`}
					</span>
				</Link>
			)}

			<div className="mt-3 flex items-start gap-2">
				<button
					type="button"
					onClick={() => void openTwetchUserProfile(userId)}
					className="h-9 flex-1 rounded-xl bg-secondary text-xs font-bold transition-colors hover:bg-muted"
				>
					View profile
				</button>
				<FollowButton userId={userId} />
			</div>
		</div>
	)
}

/// Wrap any element that names a Twetch user. `children` must be a single
/// element that accepts a ref (a button, link or span).
export default function UserHoverCard({
	userId,
	user,
	children
}: {
	userId: number | null | undefined
	/// Whatever the caller already knows (name, icon) — shown instantly while
	/// the full profile loads.
	user?: TwetchUser
	children: React.ReactElement
}) {
	if (userId === null || userId === undefined || !Number.isFinite(userId)) {
		return children
	}
	if (!featureFlags(SOCIAL_PROFILES_FLAG)) {
		return children
	}
	return (
		<HoverCard.Root openDelay={350} closeDelay={120}>
			<HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
			<HoverCard.Portal>
				<HoverCard.Content
					side="bottom"
					align="start"
					sideOffset={8}
					collisionPadding={12}
					// Portaled out of `.alkanes-surface`: `alkanes-ink` re-applies the
					// register's tokens and ink colour.
					className="alkanes-ink z-50 rounded-2xl border border-border/60 bg-popover shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 motion-reduce:animate-none"
				>
					<CardBody userId={userId} seed={user} />
				</HoverCard.Content>
			</HoverCard.Portal>
		</HoverCard.Root>
	)
}
